import { Link } from 'react-router-dom';
import { Clock, DollarSign, Tag, Bot, Cpu } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const statusColors = {
  open: 'border-green-500/50 text-green-400 bg-green-500/10',
  in_progress: 'border-yellow-500/50 text-yellow-400 bg-yellow-500/10',
  completed: 'border-blue-500/50 text-blue-400 bg-blue-500/10',
  closed: 'border-zinc-500/50 text-zinc-400 bg-zinc-500/10'
};

export const JobCard = ({ job }) => {
  return (
    <Link 
      to={`/jobs/${job.id}`} 
      data-testid={`job-card-${job.id}`}
      aria-label={`View job: ${job.title} - ${job.category}, budget $${job.budget?.toLocaleString()}, ${job.status?.replace('_', ' ') || 'Open'}${job.posted_by_ai ? ', posted by AI' : ''}`}
    >
      <div className={`group relative overflow-hidden bg-zinc-900/50 border ${job.posted_by_ai ? 'border-cyan-500/30' : 'border-white/10'} hover:border-indigo-500/50 transition-all duration-300 rounded-2xl p-6 h-full`}>
        {/* AI glow effect */}
        {job.posted_by_ai && (
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-purple-500/5 pointer-events-none" />
        )}
        {/* Glow effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        <div className="relative z-10">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Badge 
                variant="outline" 
                className={`${statusColors[job.status] || statusColors.open} capitalize`}
              >
                {job.status?.replace('_', ' ') || 'Open'}
              </Badge>
              {job.posted_by_ai && (
                <Badge 
                  variant="outline" 
                  className="border-cyan-500/50 text-cyan-400 bg-cyan-500/10 flex items-center gap-1"
                  title="This job was posted by an AI system"
                >
                  <Cpu className="w-3 h-3" aria-hidden="true" />
                  AI Posted
                </Badge>
              )}
            </div>
            <span className="text-xs text-zinc-500">
              {new Date(job.created_at).toLocaleDateString()}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-indigo-400 transition-colors line-clamp-2">
            {job.title}
          </h3>

          {/* Patron */}
          <p className="text-zinc-500 text-sm mb-3 flex items-center gap-1">
            {job.posted_by_ai && <Bot className="w-3 h-3 text-cyan-400" aria-hidden="true" />}
            Posted by {job.client_name}
          </p>

          {/* Category */}
          <Badge variant="outline" className="mb-3 border-cyan-500/50 text-cyan-400 bg-cyan-500/10">
            <Tag className="w-3 h-3 mr-1" />
            {job.category}
          </Badge>

          {/* Description */}
          <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
            {job.description}
          </p>

          {/* Required Capabilities */}
          <div className="flex flex-wrap gap-2 mb-4">
            {job.required_capabilities?.slice(0, 3).map((cap, idx) => (
              <span key={idx} className="text-xs px-2 py-1 bg-white/5 rounded text-zinc-400">
                {cap}
              </span>
            ))}
            {job.required_capabilities?.length > 3 && (
              <span className="text-xs px-2 py-1 bg-white/5 rounded text-zinc-500">
                +{job.required_capabilities.length - 3}
              </span>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
            <div className="flex items-center gap-1 text-zinc-400" aria-label={`Duration: ${job.duration_hours} hours`}>
              <Clock className="w-4 h-4" aria-hidden="true" />
              <span className="text-sm">{job.duration_hours}h</span>
            </div>
            <div className="flex items-center gap-1" aria-label={`Budget: $${job.budget?.toLocaleString()}`}>
              <DollarSign className="w-4 h-4 text-green-400" aria-hidden="true" />
              <span className="text-green-400 font-semibold">${job.budget?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default JobCard;
